/**
 * @file tariffs.ts
 * @description Тарифный справочник: все ставки, коэффициенты и пороги для расчёта таможенных платежей.
 *              Данные соответствуют нормативке РФ на дату lastUpdated.
 * @rule НЕ менять числовые значения без явного согласования — они сверены с нормативными документами
 * @rule При изменении ставок обновить lastUpdated и validUntil
 * @rule Утильсбора здесь НЕТ: его коэффициенты живут только в проверенном справочнике
 *       src/data/tariffs/recycling-pp1291-section1.json (кВт, редакции по годам), расчёт —
 *       src/lib/tariffs/recyclingFee.ts (CALC-NORM-01, 2026-09-30).
 * @sources ПП РФ №1291 в ред. ПП РФ №1713 от 01.11.2025 (утильсбор — справочник), ПП РФ №1638 от 23.10.2025 (новые ставки таможенного сбора с 01.01.2026), ФЗ №425-ФЗ от 28.11.2025 (НДС 22% с 01.01.2026), Решение ЕАЭС 2017 (ЕТС), НК РФ ст.193 (акциз), ПП РФ №863 (таможенный сбор), Решение Совета ЕЭК от 14.09.2021 № 80 (пошлина 15% на электромобили)
 */

// Метаданные актуальности
export const TARIFF_META = {
  lastUpdated: '2026-09-30',
  validUntil: '2026-12-31',
  sources: [
    'ПП РФ №1291 (ред. 06.02.2026) — базовый закон об утильсборе, категория М1',
    'ПП РФ №1713 от 01.11.2025 — раздел I в кВт, коэффициенты 2026 (справочник src/data/tariffs/recycling-pp1291-section1.json)',
    'Решение Коллегии ЕЭК №81 от 23.09.2025 — детализация ТН ВЭД 8703 80: BEV (8703 80 000 3) и REEV/EREV (8703 80 000 5)',
    'Решение Совета ЕЭК от 14.09.2021 № 80 — пошлина 15% на электромобили (ТН ВЭД 8703 80)',
    'Последовательные гибриды (EREV/REEV: Lixiang, Voyah, Aito) — пошлина 15% и утильсбор по EV-таблице (источники: vlb-broker, leon-kuzovnoj, gt-news, cncars)',
    'Решение ЕАЭС от 2017 — единые ставки ЕТС для физлиц (ДВС)',
    'НК РФ ст.193 — акциз по мощности двигателя',
    'ПП РФ №863 — таможенный сбор по стоимости авто',
    'ЕТТ ЕАЭС — раздельные ставки бензин/дизель для юрлиц',
    'ПП РФ №1638 от 23.10.2025 — новые ставки таможенного сбора за оформление с 01.01.2026',
    'ФЗ №425-ФЗ от 28.11.2025 — НДС повышен с 20% до 22% с 01.01.2026 (НК РФ ст. 164)',
  ],
  monitorUrls: [
    { url: 'https://customs.gov.ru', description: 'Утильсбор, таможенные сборы' },
    { url: 'https://pravo.gov.ru', description: 'Постановления правительства' },
    { url: 'https://eec.eaeunion.org', description: 'Решения ЕЭК, ЕТТ, ЕТС' },
    { url: 'https://www.consultant.ru/document/cons_doc_LAW_156832/', description: 'ПП РФ №1291 в актуальной редакции' },
  ],
} as const;

/* ── 2.1 Сбор за таможенное оформление (с 01.01.2026, ПП РФ №1638; corrected 2026-05-21) ─── */
export const CUSTOMS_PROCESSING_FEE: { maxRub: number; fee: number }[] = [
  { maxRub: 200_000, fee: 1_231 },
  { maxRub: 450_000, fee: 2_462 },
  { maxRub: 1_200_000, fee: 4_924 },
  { maxRub: 2_700_000, fee: 13_541 },
  { maxRub: 4_200_000, fee: 18_465 },
  { maxRub: 5_500_000, fee: 21_344 },
  { maxRub: 10_000_000, fee: 49_240 },
  { maxRub: Infinity, fee: 73_860 },
];

/* ── 2.2 ЕТС — Единая таможенная ставка (физлица) ────────────────── */

// Авто до 3 лет
export const ETS_UNDER3: { maxEur: number; pct: number; minPerCc: number }[] = [
  { maxEur: 8_500, pct: 0.54, minPerCc: 2.5 },
  { maxEur: 16_700, pct: 0.48, minPerCc: 3.5 },
  { maxEur: 42_300, pct: 0.48, minPerCc: 5.5 },
  { maxEur: 84_500, pct: 0.48, minPerCc: 7.5 },
  { maxEur: 169_000, pct: 0.48, minPerCc: 15.0 },
  { maxEur: Infinity, pct: 0.48, minPerCc: 20.0 },
];

// Авто 3–5 лет
export const ETS_3TO5: { maxCc: number; rate: number }[] = [
  { maxCc: 1000, rate: 1.5 },
  { maxCc: 1500, rate: 1.7 },
  { maxCc: 1800, rate: 2.5 },
  { maxCc: 2300, rate: 2.7 },
  { maxCc: 3000, rate: 3.0 },
  { maxCc: Infinity, rate: 3.6 },
];

// Авто старше 5 лет
export const ETS_OVER5: { maxCc: number; rate: number }[] = [
  { maxCc: 1000, rate: 3.0 },
  { maxCc: 1500, rate: 3.2 },
  { maxCc: 1800, rate: 3.5 },
  { maxCc: 2300, rate: 4.8 },
  { maxCc: 3000, rate: 5.0 },
  { maxCc: Infinity, rate: 5.7 },
];

/* ── 2.3 Таможенная пошлина (юрлица) — БЕНЗИН ───────────────────── */

// Бензин до 3 лет — зависит от объёма
export const DUTY_PETROL_UNDER3: { maxCc: number; pct: number }[] = [
  { maxCc: 2800, pct: 0.15 },
  { maxCc: Infinity, pct: 0.125 },
];

// Бензин 3–7 лет: 20%, но не менее мин. ставки за см³
export const DUTY_PETROL_3TO7: { maxCc: number; pct: number; minPerCc: number }[] = [
  { maxCc: 1000, pct: 0.20, minPerCc: 0.36 },
  { maxCc: 1500, pct: 0.20, minPerCc: 0.40 },
  { maxCc: 1800, pct: 0.20, minPerCc: 0.36 },
  { maxCc: 2300, pct: 0.20, minPerCc: 0.44 },
  { maxCc: 2800, pct: 0.20, minPerCc: 0.44 },
  { maxCc: 3000, pct: 0.20, minPerCc: 0.44 },
  { maxCc: Infinity, pct: 0.20, minPerCc: 0.80 },
];

// Бензин старше 7 лет — фиксированная ставка
export const DUTY_PETROL_OVER7: { maxCc: number; rate: number }[] = [
  { maxCc: 1000, rate: 1.4 },
  { maxCc: 1500, rate: 1.5 },
  { maxCc: 1800, rate: 1.6 },
  { maxCc: 2300, rate: 2.2 },
  { maxCc: 2800, rate: 2.2 },
  { maxCc: 3000, rate: 2.2 },
  { maxCc: Infinity, rate: 3.2 },
];

/* ── 2.3b Таможенная пошлина (юрлица) — ДИЗЕЛЬ ──────────────────── */

// Дизель до 3 лет — 15% для всех объёмов
export const DUTY_DIESEL_UNDER3_PCT = 0.15;

// Дизель 3–7 лет: 20%, но не менее мин. ставки за см³
export const DUTY_DIESEL_3TO7: { maxCc: number; pct: number; minPerCc: number }[] = [
  { maxCc: 1500, pct: 0.20, minPerCc: 0.32 },
  { maxCc: 2500, pct: 0.20, minPerCc: 0.40 },
  { maxCc: Infinity, pct: 0.20, minPerCc: 0.80 },
];

// Дизель старше 7 лет — фиксированная ставка
export const DUTY_DIESEL_OVER7: { maxCc: number; rate: number }[] = [
  { maxCc: 1500, rate: 1.5 },
  { maxCc: 2500, rate: 2.2 },
  { maxCc: Infinity, rate: 3.2 },
];

/* ── 2.3c Электромобили (ТН ВЭД 8703 80) ─────────────────────────── */
// ELECTRIC_DUTY_RATE — в секции 2.6.

/* ── 2.4 Акциз (2026, ФЗ №425-ФЗ от 28.11.2025) ─────────────────── */
// @rule Сверено 30.09.2026 с НК РФ ст.193 п.1 (ставки «с 1 января по 31 декабря 2026 года»).
export const EXCISE_RATES: { maxHp: number; rate: number }[] = [
  { maxHp: 90, rate: 0 },
  { maxHp: 150, rate: 64 },
  { maxHp: 200, rate: 613 },
  { maxHp: 300, rate: 1_004 },
  { maxHp: 400, rate: 1_711 },
  { maxHp: 500, rate: 1_771 },
  { maxHp: Infinity, rate: 1_829 },
];

/* ── 2.5 НДС — 22% (с 01.01.2026, ФЗ №425-ФЗ от 28.11.2025) ───── */
export const VAT_RATE = 0.22;

/* ── 2.6 Электромобили и последовательные гибриды: пошлина ─────────── */

/**
 * Пошлина 15% — применяется для электромобилей (BEV, ТН ВЭД 8703 80 000 3) И
 * для последовательных гибридов (REEV/EREV, ТН ВЭД 8703 80 000 5).
 *
 * @rule Любой возраст, любой статус (физ/юр).
 * @rule Подтверждено: vlb-broker.ru, leon-kuzovnoj.ru, gt-news.ru, cncars.by
 *       (статья от 22.01.2026, ссылка на Решение Коллегии ЕЭК №81 от 23.09.2025).
 * @todo EREV у физлица: 15% или ЕТС — вопрос владельца В2 открыт, до решения считаем как
 *       электромобиль (knowledge/roadmap/tasks/2026-09-30-erev-duty-v2.md).
 */
export const ELECTRIC_DUTY_RATE = 0.15;

/* ── 2.7 Фиксированные расходы ────────────────────────────────────── */
export const FIXED_COSTS = {
  china: {
    markup_cny: 16_000,    // Наценка в юанях (все расходы Китай)
    markup_rub: 150_000,   // Наценка в рублях (все расходы Россия, включая доставку в Уссурийск)
  },
  korea: {
    /**
     * `importMarkupKrw` — markup in ₩ added to car price BEFORE currency conversion.
     * Covers Korea-side expenses: domestic delivery in Korea, document handling,
     * SBKTS preparation, EPTS preparation, freight Korea→Vladivostok.
     * Included in customs value (price + costs to the border).
     */
    importMarkupKrw: 1_700_000,
    /** Customs broker fees in RUB. */
    brokerRub: 100_000,
    /** JCK AUTO commission in RUB. */
    commissionRub: 100_000,
  },
  japan: {
    /**
     * `importMarkupJpy` — markup in ¥ added to car price BEFORE currency conversion.
     * Covers Japan-side expenses: domestic delivery in Japan, document handling,
     * SBKTS preparation, EPTS preparation, freight Japan→Vladivostok.
     * Included in customs value (price + costs to the border).
     */
    importMarkupJpy: 120_000,
    /** Customs broker fees in RUB. */
    brokerRub: 65_000,
    /** JCK AUTO commission in RUB. */
    commissionRub: 50_000,
  },
} as const;

export const DELIVERY_CITY: Record<string, string> = {
  china: "Уссурийск",
  korea: "Владивосток",
  japan: "Владивосток",
};
